import { useState } from 'react';
import { Typography } from 'antd';
import { PhoneOutlined, UserOutlined, ClockCircleOutlined } from '@ant-design/icons';
import GradientAvatar from '../shared/GradientAvatar';
import type { Customer } from '../../types/business';

const { Text } = Typography;

const LOCALE_MAP: Record<string, string> = {
  uz: 'uz-UZ',
  ru: 'ru-RU',
  en: 'en-US',
};

function formatPhone(phone: string): string {
  if (phone.length === 12 && phone.startsWith('998')) {
    return `+${phone.slice(0, 3)} ${phone.slice(3, 5)} ${phone.slice(5, 8)} ${phone.slice(8)}`;
  }
  return phone.startsWith('+') ? phone : `+${phone}`;
}

function getInitials(customer: Customer): string {
  const fn = customer.firstName?.[0] ?? '';
  const ln = customer.lastName?.[0] ?? '';
  const combined = (fn + ln).trim();
  if (combined) return combined.toUpperCase();
  if (customer.username) return customer.username[0].toUpperCase();
  return '';
}

interface Props {
  customer: Customer;
  language: string;
  fallbackName: string;
  onClick?: (customer: Customer) => void;
}

export default function CustomerRow({ customer, language, fallbackName, onClick }: Props) {
  const [pressed, setPressed] = useState(false);
  const initials = getInitials(customer);

  const displayName: string = (() => {
    if (customer.firstName || customer.lastName) {
      return [customer.firstName, customer.lastName].filter(Boolean).join(' ');
    }
    if (customer.username) return `@${customer.username}`;
    if (customer.phoneNumber) return formatPhone(customer.phoneNumber);
    return fallbackName;
  })();

  const locale = LOCALE_MAP[language] ?? 'uz-UZ';
  const dateLabel = new Date(customer.createdAt).toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const interactive = onClick !== undefined;

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={() => onClick?.(customer)}
      onKeyDown={(e) => {
        if (interactive && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.(customer);
        }
      }}
      onTouchStart={() => interactive && setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      onMouseDown={() => interactive && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        background: 'var(--bl-surface, #fff)',
        border: '1px solid var(--bl-divider, #ebebeb)',
        borderRadius: 14,
        boxShadow: pressed
          ? '0 1px 4px rgba(0,0,0,0.04)'
          : 'var(--bl-shadow-soft, 0 2px 10px rgba(0,0,0,0.04))',
        cursor: interactive ? 'pointer' : 'default',
        transform: pressed ? 'scale(0.985)' : 'scale(1)',
        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
      }}
    >
      <GradientAvatar
        id={customer.id}
        initials={initials || undefined}
        fallbackIcon={<UserOutlined />}
        size={42}
      />

      <div style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            display: 'block',
            fontSize: 14,
            fontWeight: 700,
            color: 'var(--bl-text-strong, #0f172a)',
            letterSpacing: -0.1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginBottom: 3,
          }}
        >
          {displayName}
        </Text>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            fontSize: 11,
            color: 'var(--bl-text-muted, #6b7280)',
          }}
        >
          {customer.phoneNumber && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontVariantNumeric: 'tabular-nums' }}>
              <PhoneOutlined style={{ fontSize: 10, opacity: 0.6 }} />
              {formatPhone(customer.phoneNumber)}
            </span>
          )}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--bl-text-muted, #9ca3af)' }}>
            <ClockCircleOutlined style={{ fontSize: 9, opacity: 0.55 }} />
            {dateLabel}
          </span>
        </div>
      </div>
    </div>
  );
}
